import { clearSubmitLeadsDraft } from '../lib/submitLeadsValidation';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { LogOut, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface AgentProfileInfo {
  display_name?: string | null;
  email: string;
  agent_code: string;
  is_active: boolean;
  is_master?: boolean;
}

export default function AgentProfileDropdown() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<AgentProfileInfo | null>(null);

  useEffect(() => {
    const load = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      const { data } = await supabase
        .from('agent_profiles')
        .select('agent_code, display_name, is_active, is_master')
        .eq('user_id', session.user.id)
        .single();
      if (data) {
        setProfile({
          ...data,
          email: session.user.email || '',
        });
      } else {
        setProfile({
          display_name: null,
          email: session.user.email || '',
          agent_code: '',
          is_active: true,
          is_master: false,
        });
      }
    };
    load();
  }, []);

  const handleLogout = async () => {
    clearSubmitLeadsDraft();
    await supabase.auth.signOut();
    toast.success('Logged out');
    const { portalPath } = await import('@/shared/app/portalPath');
    navigate(portalPath('agent')('/login'));
  };

  const emailPrefix = profile?.email?.split('@')[0] ?? '';
  const codeUpper = profile?.agent_code?.toUpperCase() ?? '';
  const isMasterEmail = profile?.email?.toLowerCase() === 'agentmaster@khmerhealthvn.com';
  const displayName =
    codeUpper ||
    profile?.display_name?.trim() ||
    (isMasterEmail ? 'Agent Master' : emailPrefix.toUpperCase()) ||
    'Agent';
  const roleLabel = profile?.is_master ? 'Agent Master' : 'Agent';
  const initials = (displayName || 'A')
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5 h-8 px-2 text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10">
          <div className="h-6 w-6 rounded-full bg-primary-foreground/20 flex items-center justify-center text-[10px] font-semibold">
            {initials}
          </div>
          <span className="hidden sm:inline text-xs font-medium max-w-[140px] truncate">
            {displayName}
          </span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="font-normal">
          <div className="space-y-1.5">
            <p className="text-sm font-medium">{displayName}</p>
            <p className="text-xs text-muted-foreground break-all">{profile?.email}</p>
            <p className="text-xs text-muted-foreground">Role: {roleLabel}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive cursor-pointer">
          <LogOut className="h-3.5 w-3.5 mr-2" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
